import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AionLogoArtwork } from "@/components/aion/AionLogoArtwork";
import { acceptCustomerOwnerInvitation, resendVerification } from "@/services/api";

const readInviteToken = () => {
  const hashParams = new URLSearchParams(String(window.location.hash || "").replace(/^#/, ""));
  const queryParams = new URLSearchParams(window.location.search);
  return hashParams.get("token") || queryParams.get("token") || "";
};

export const CustomerOwnerInviteScreen = () => {
  const [token] = useState(readInviteToken);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [pending, setPending] = useState(false);
  const [acceptedUser, setAcceptedUser] = useState(null);
  const [verificationPending, setVerificationPending] = useState(false);
  const [verificationFeedback, setVerificationFeedback] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (token) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [token]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!token) {
      setError("Der Einladungslink enthält keinen gültigen Token.");
      return;
    }
    if (password.length < 8) {
      setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwörter stimmen nicht überein.");
      return;
    }
    if (!acceptedPrivacy || !acceptedTerms) {
      setError("Bitte Datenschutz und Nutzungsbedingungen ausdrücklich bestätigen.");
      return;
    }

    setPending(true);
    try {
      const user = await acceptCustomerOwnerInvitation({
        token,
        password,
        accepted_privacy: acceptedPrivacy,
        accepted_terms: acceptedTerms,
      });
      setAcceptedUser(user);
      setPassword("");
      setConfirmPassword("");
    } catch (requestError) {
      setError(requestError?.message || "Einladung konnte nicht angenommen werden.");
    } finally {
      setPending(false);
    }
  };

  const requestEmailVerification = async () => {
    if (!acceptedUser?.email) return;
    setVerificationPending(true);
    setError("");
    setVerificationFeedback("");
    try {
      const response = await resendVerification({ email: acceptedUser.email });
      const verifyPath = response?.preview_links?.verify_path;
      if (verifyPath) {
        window.location.assign(verifyPath);
        return;
      }
      setVerificationFeedback("Bestätigungs-E-Mail wurde vorbereitet. Öffne den Link aus deiner E-Mail und melde dich danach an.");
    } catch (requestError) {
      setError(requestError?.message || "E-Mail-Bestätigung konnte nicht gestartet werden.");
    } finally {
      setVerificationPending(false);
    }
  };

  const goToLogin = () => {
    window.history.replaceState({}, document.title, "/");
    window.location.assign("/");
  };

  return (
    <div className="dashboard-shell" data-testid="customer-owner-invite-screen">
      <div className="dashboard-background" />
      <Card className="login-card auth-card" data-testid="customer-owner-invite-card">
        <CardHeader>
          <Badge variant="outline" className="w-fit">AION OWNER</Badge>
          <AionLogoArtwork variant="main" className="aion-auth-brand" imageClassName="aion-auth-brand-image" dataTestId="customer-owner-invite-logo" />
          <CardTitle className="login-title">AION Einrichtung übernehmen</CardTitle>
          <CardDescription>
            Lege dein Passwort fest und übernimm den bereits vorbereiteten Customer-Owner-Zugang.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? <div className="error-box" data-testid="customer-owner-invite-error">{error}</div> : null}

          {acceptedUser ? (
            <div className="login-form" data-testid="customer-owner-invite-success">
              <div className="aion-note">
                Einladung angenommen. Dein Customer-Owner-Konto {acceptedUser.email ? `(${acceptedUser.email})` : ""} ist vorbereitet.
              </div>
              {acceptedUser.email_verified ? (
                <div className="aion-note" data-testid="customer-owner-invite-email-verified">
                  Deine E-Mail ist bereits verifiziert. Du kannst dich jetzt anmelden.
                </div>
              ) : (
                <>
                  <div className="aion-note" data-testid="customer-owner-invite-email-verification-required">
                    Die Einladung bestätigt noch nicht den Besitz deiner E-Mail-Adresse. Vor der Anmeldung ist deshalb eine echte E-Mail-Bestätigung erforderlich.
                  </div>
                  {verificationFeedback ? <div className="aion-note" data-testid="customer-owner-invite-verification-feedback">{verificationFeedback}</div> : null}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void requestEmailVerification()}
                    disabled={verificationPending || !acceptedUser.email}
                    data-testid="customer-owner-invite-send-verification-button"
                  >
                    {verificationPending ? "Wird vorbereitet …" : "Bestätigungs-E-Mail senden"}
                  </Button>
                </>
              )}
              <Button type="button" className="login-button" onClick={goToLogin} data-testid="customer-owner-invite-login-button">
                Zur Anmeldung
              </Button>
            </div>
          ) : (
            <form className="login-form" onSubmit={submit} data-testid="customer-owner-invite-form">
              {!token ? <div className="aion-note">Der Einladungslink ist unvollständig oder wurde bereits aus der URL entfernt.</div> : null}
              <label className="login-field">
                <span>Neues Passwort</span>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  data-testid="customer-owner-invite-password"
                />
              </label>
              <label className="login-field">
                <span>Passwort bestätigen</span>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  data-testid="customer-owner-invite-password-confirm"
                />
              </label>
              <label className="auth-checkbox-row">
                <input
                  type="checkbox"
                  checked={acceptedPrivacy}
                  onChange={(event) => setAcceptedPrivacy(event.target.checked)}
                  data-testid="customer-owner-invite-privacy"
                />
                <span>Ich akzeptiere Datenschutz und sichere Verarbeitung.</span>
              </label>
              <label className="auth-checkbox-row">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(event) => setAcceptedTerms(event.target.checked)}
                  data-testid="customer-owner-invite-terms"
                />
                <span>Ich akzeptiere die Nutzungsbedingungen.</span>
              </label>
              <Button
                type="submit"
                className="login-button"
                disabled={pending || !token}
                data-testid="customer-owner-invite-submit"
              >
                {pending ? "Zugang wird vorbereitet …" : "Einladung annehmen"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
